/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import React, { useEffect, useMemo } from 'react';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { SFXContext } from './sfx-context';
import { Howl, Howler } from 'howler';

/**
 *
 * @examle
 * const sfx = useGlobalSFX();
 * sfx.newMemberConnected.play();
 *
 * @param children
 * @constructor
 */
export const GlobalSFXProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sfxVolume } = useSettings();

  useEffect(() => {
    Howler.autoUnlock = true;
    Howler.volume(sfxVolume);
    const resumeCtx = () => Howler.ctx?.resume?.();
    document.addEventListener('click', resumeCtx, { once: true });
    return () => document.removeEventListener('click', resumeCtx);
  }, [sfxVolume]);

  const createSFX = (
    src: string | string[],
    volume = sfxVolume,
    loop = false
  ) =>
    // HTML5 Audio pool exhausted, returning potentially locked audio object
    new Howl({
      src: Array.isArray(src) ? src : [src],
      volume,
      preload: true,
      loop,
      html5: true, // use <audio> element to avoid WebAudioContext conflicts
    });

  const sounds = useMemo(() => {
    return {
      raiseHand: createSFX('/assets/audio/snap_sfx.wav'),
      lowerHand: createSFX('/assets/audio/snap_sfx.wav'),
      newMemberConnected: createSFX('/assets/audio/connected-member-1.wav'),
      disconnectedMember: createSFX('/assets/audio/disconnected-member-1.wav'),
      reconnecting: createSFX('/assets/audio/connecting_1.wav'),
      disconnected: createSFX('/assets/audio/disconnecting.wav'),
      microphoneMuteUnmute: createSFX('/assets/audio/mute-unmute.wav'),
      microphoneMute: createSFX('/assets/audio/mic-mute.wav'),
      microphoneUnmute: createSFX('/assets/audio/mic-unmute.wav'),
      speakingWithMute: createSFX('/assets/audio/mic-mute-speaking.wav'),
      cameraToggle: createSFX('/assets/audio/camera-toggle.wav', 0.3),
      cameraOn: createSFX('/assets/audio/cam-on.wav', 0.3),
      cameraOff: createSFX('/assets/audio/cam-off.wav', 0.3),
      loading: createSFX('/assets/audio/loading_call.wav', sfxVolume, true),
      connectChannel: createSFX('/assets/audio/channel_connect_1.wav'),
      appear: createSFX('/assets/audio/connect_channel.wav'),
      disappear: createSFX('/assets/audio/disconnect_channel.wav'),
      clap: createSFX('/assets/audio/snap_sfx.wav'),
      hahaLaughCartoonBully: createSFX(
        '/assets/audio/ha-ha-laugh-cartoon-bully.wav'
      ),
      punchlineDrumStingSoundroll: createSFX(
        '/assets/audio/punchline-drum-sting-soundroll.wav'
      ),
      pupUpBubble: createSFX('/assets/audio/pup-up-bubble.wav'),
      whistlingTwitTwoo: createSFX('/assets/audio/whistling-twit-twoo.wav'),
      newMessage: createSFX('/assets/audio/new-message-pop.wav', 0.2),
      incomingCall: createSFX('/assets/audio/loading-screen.wav', 0.4),
      screenShareOn: createSFX('/assets/audio/screen-share-on.wav', 0.3),
      screenShareOff: createSFX('/assets/audio/screen-share-off1.wav', 0.3),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // instantiate once only

  return <SFXContext.Provider value={sounds}>{children}</SFXContext.Provider>;
};
